import * as React from "react";
import {
  EditorRegistry,
  ElementEditConfig,
  ElementEditConfigRegistry,
  ElementRulesEditorViewComponentType,
  TabLiterals,
} from "@graffer-inc/form-builder";
import {
  CloudGovFormSchema,
  ReservedApplicantIdHelper,
  SmartApplyFormSchemaValidator,
  CloudGovFormSchemaAccessor,
  isReplacedApplicantCorporateSearchComponent,
  ValidateOpt,
  ApplicantSchemaKeys,
  getTotalMaxFileSize,
} from "@graffer-inc/cloud-gov-core";
import {
  FormSchema,
  IFieldElementUnion,
  IFormSchemaV2,
  IRepeatableElement,
  ISectionElement,
  SchemaBuilder,
  isFileInputElement,
  isInputElement,
} from "@graffer-inc/form-schema";
import { ApplicantElementEditorViewFactory } from "./custom-components/applicant-schema/applicant-element-editor";
import {
  DefaultEditorRegistry,
  DefaultElementConfigRegistry,
  DefaultToolbarItemList,
  FormEditor,
} from "../../../../common/form-builder-wrapper";
import { ElementRulesEditorView } from "../../common/form-editor/published-schema-update-custom-editor/element-rules-editor-view";
import { SmartApplyFileEditorFactory } from "./custom-components/file-input/smart-apply-file-editor-factory";
import { FeatureFlags } from "../../../../feature-flags";
import { formMarkdownProcessorFactory } from "../../../../utils/markdown/processor";
import { grafferSchemasSuportTagNames } from "../../../../utils/markdown/constants";

const processor = formMarkdownProcessorFactory(grafferSchemasSuportTagNames);
const smartApplyGrafferSchemasMarkdownProcessor = (content: string) => {
  return processor.processSync(content).result as React.ReactNode;
};

const ToolbarItems = [...DefaultToolbarItemList];

export type BaseProps = {
  schemaBuilder: SchemaBuilder;
  setNewBuilder: (newBuilder: SchemaBuilder) => void;
};

export const CloudGovFormEditor: React.FC<{
  initialSchema: IFormSchemaV2;
  applicantSchemaKeys: ApplicantSchemaKeys;
  initialSection?: number;
  initialTab?: TabLiterals;
  validateOpt: ValidateOpt;
  currentFormSchema: IFormSchemaV2;
  onChange: (schema: IFormSchemaV2) => void;
  onChangeCurrentSection?: (sectIndex: number) => void;
  onChangeCurrentTab?: (tab: TabLiterals) => void;
  allowCustomElemId?: boolean;
  isSafeEditOnly: boolean;
}> = ({
  initialSchema,
  initialSection,
  initialTab,
  validateOpt,
  onChangeCurrentSection,
  onChangeCurrentTab,
  currentFormSchema,
  onChange,
  allowCustomElemId,
  applicantSchemaKeys,
  isSafeEditOnly,
}) => {
  const validator = new SmartApplyFormSchemaValidator(
    currentFormSchema,
    applicantSchemaKeys,
  );
  const errors = validator.validateDeep(validateOpt);
  const cloudGovSchema = CloudGovFormSchema.fromJSON(currentFormSchema);
  const accessor = new CloudGovFormSchemaAccessor(
    currentFormSchema,
    applicantSchemaKeys,
  );
  const ApplicantElementEditorView = React.useMemo(() => {
    return ApplicantElementEditorViewFactory(
      applicantSchemaKeys,
      isSafeEditOnly,
    );
  }, [applicantSchemaKeys, isSafeEditOnly]);
  const viewMode = isSafeEditOnly ? "READONLY" : "EDITABLE";

  return (
    <>
      {errors.length > 0 && (
        <ul className="notification is-danger is-light">
          {errors.map((m) => (
            <li>{m}</li>
          ))}
        </ul>
      )}
      <FormEditor
        initialSchema={initialSchema}
        initialSection={initialSection}
        // TODO: Fix type
        onChange={(schema) => onChange(FormSchema.fromJSON(schema))}
        onChangeCurrentSection={onChangeCurrentSection}
        editorOpt={{
          allowCustomElemId: allowCustomElemId,
          toolbarItemList: ToolbarItems,
          formulaViewMode: viewMode,
          conditionalRuleViewMode: viewMode,
          editConfigRegistry: new (class implements ElementEditConfigRegistry {
            get(
              elem: IFieldElementUnion,
              repeatable?: IRepeatableElement,
            ): ElementEditConfig {
              if (isSafeEditOnly) {
                return {
                  disableSort: true,
                  disableInsertion: true,
                  // 「申請者の種別」だけは既存の振る舞いに従い編集不可とする
                  disableEdit: ReservedApplicantIdHelper.isApplicantType(
                    elem.id,
                    applicantSchemaKeys,
                  ),
                  disableDelete: true,
                  disableCopy: true,
                };
              }
              if (
                ReservedApplicantIdHelper.contains(elem.id, applicantSchemaKeys)
              ) {
                return {
                  disableSort: true,
                  disableInsertion: true,
                  disableEdit: ReservedApplicantIdHelper.isApplicantType(
                    elem.id,
                    applicantSchemaKeys,
                  ),
                  disableDelete: true,
                  disableCopy: true,
                };
              }
              return DefaultElementConfigRegistry.get(elem, repeatable);
            }
            getSectionEditConfig(elem: ISectionElement) {
              if (isSafeEditOnly) {
                return {
                  ...DefaultElementConfigRegistry.getSectionEditConfig(elem),
                  disableSort: true,
                  disableAdd: true,
                  disableEdit: true,
                  disableDelete: true,
                  disableCopy: true,
                };
              }
              if (
                ReservedApplicantIdHelper.isApplicantSectionId(
                  elem.id,
                  applicantSchemaKeys,
                )
              ) {
                return {
                  ...DefaultElementConfigRegistry.getSectionEditConfig(elem),
                  disableSort: true,
                  disableDelete: true,
                  disableAdd: true,
                  disableCopy: true,
                };
              }
              return DefaultElementConfigRegistry.getSectionEditConfig(elem);
            }
          })(),
          editorRegistry: new (class implements EditorRegistry {
            getElementEditor(
              elem: IFieldElementUnion,
              parent?: IRepeatableElement,
            ): ElementRulesEditorViewComponentType | undefined {
              // 申請者セクションの項目は isSafeEditOnly の値に関わらず、
              // 特殊な表示ロジックがあるために必ず ApplicantElementEditorView を返す。
              // isSafeEditOnly の値に応じた処理は ApplicantElementEditorView 内で行う。
              if (
                ReservedApplicantIdHelper.contains(elem.id, applicantSchemaKeys)
              ) {
                return ApplicantElementEditorView;
              }
              if (isSafeEditOnly) {
                return ElementRulesEditorView;
              }
              if (isInputElement(elem) && isFileInputElement(elem)) {
                return SmartApplyFileEditorFactory(
                  getTotalMaxFileSize(currentFormSchema),
                  elem,
                  parent,
                );
              }
              return DefaultEditorRegistry.getElementEditor(elem, parent);
            }
            getBuilderFieldTemplate<
              T extends IFieldElementUnion = IFieldElementUnion,
            >(elem: T, parent?: IRepeatableElement) {
              // 従来の挙動へ合わせて、申請者情報内の法人検索コンポーネントを非表示とする
              if (
                isReplacedApplicantCorporateSearchComponent(
                  elem,
                  applicantSchemaKeys,
                )
              ) {
                return () => null;
              }

              return DefaultEditorRegistry.getBuilderFieldTemplate(
                elem,
                parent,
              );
            }
            getDropdownMenuContent():
              | React.ComponentType<BaseProps>
              | undefined {
              return undefined;
            }
          })(),
          sectionLimit: isSafeEditOnly
            ? currentFormSchema.root.length // セクション追加ボタンを非表示にするために現在のセクション数を指定する。 see: https://github.com/graffer-inc/graffer-schemas/blob/70784b3c071948bd221f8268b58112603a8d87fd/libs/packages/form-builder/lib/elements/main-pane/section/section-tabs.tsx#L119-L120
            : undefined,
          supportMyNumberInput: true,
          supportFileInput: true,
          supportFormula: true,
          supportFormulaResult: true,
          supportAllowedTermValidationForDateInput: true,
          supportPostalCode: true,
          supportCorporateSearch: true,
          supportDescription: true,
          supportImage: FeatureFlags.ENABLE_GRAFFER_SCHEMAS_STATIC_IMAGE,
          supportCopyValues: true,
          supportYearMonthInput: true,
          supportSwitchEditorModeForSelectableInput: true,
          supportSuffix: true,
          thenInputFilter: (e) => {
            return !ReservedApplicantIdHelper.contains(
              e.id,
              applicantSchemaKeys,
            );
          },
          shouldHide: (_, index) => {
            return cloudGovSchema.isApplicantSectionConditionalRules(
              index,
              applicantSchemaKeys,
            );
          },
          insertRuleIndex: accessor.userInsertIndex(),
          initialTab,
          onChangeCurrentTab,

          markdownConfig: {
            initialValueForCommonProperties: true,
            initialValueForStaticDescription: true,
            showMarkdownApplyBanner: true,
            markdownProcessor: smartApplyGrafferSchemasMarkdownProcessor,
          },
        }}
      />
    </>
  );
};
